import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, Clock, Users, TrendingUp, Download, AlertCircle, CheckCircle, Star, Phone } from 'lucide-react';
import './TourDetailPage.css';

export default function TourDetailPage() {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState('overview');

  // Mock-Daten (später aus API)
  const tour = {
    id: '1',
    title: 'NC500 Highlands Explorer - 10 Tage Rundtour',
    shortDescription: 'Die legendäre North Coast 500 durch Schottlands spektakulärste Landschaften',
    highlights: [
      'Dramatische Küstenstraßen und einsame Highlands',
      'Besuch von 5 Whisky-Destillerien entlang der Route',
      'Übernachtungen in authentischen B&Bs und Highland Lodges'
    ],
    images: [
      'https://images.unsplash.com/photo-1590521305084-9c3f1e4c7f8e?w=800',
      'https://images.unsplash.com/photo-1552074284-5e88ef1aef18?w=800',
      'https://images.unsplash.com/photo-1486299267070-83823f5448dd?w=800'
    ],
    
    // Daten
    dates: ['15.06.2025 - 24.06.2025', '20.07.2025 - 29.07.2025', '10.08.2025 - 19.08.2025'],
    startTime: '09:00 Uhr',
    endTime: '17:00 Uhr (Tag 10)',
    duration: '10 Tage / 9 Nächte',
    meetingPoint: 'Inverness Train Station, Station Square, IV1 1LE',
    returnPoint: 'Inverness Train Station',
    
    // Strecke
    distance: 850,
    elevationGain: 8500,
    elevationLoss: 8500,
    surface: 'Asphalt (90%), Schotter (10%)',
    routeType: 'Rundtour',
    gpxAvailable: true,
    
    // Anforderungen
    technicalLevel: 3,
    fitnessLevel: 4,
    minAge: 16,
    requirements: [
      'Gute Grundkondition für bis zu 100 km/Tag',
      'Erfahrung mit mehrtägigen Radtouren',
      'Bereitschaft für wechselhaftes Wetter'
    ],
    
    // Leistungen
    included: [
      'Erfahrener Guide (Deutsch/Englisch)',
      'Gepäcktransport zwischen Unterkünften',
      '9x Übernachtung in B&Bs (DZ)',
      '9x Frühstück',
      'Detailliertes Roadbook & GPX-Tracks',
      'Pannenhilfe & Begleitfahrzeug',
      'Eintritt zu 2 Destillerien'
    ],
    excluded: [
      'An- und Abreise nach Inverness',
      'Fahrrad & Helm (Mietoption verfügbar)',
      'Mittagessen & Abendessen',
      'Persönliche Ausgaben'
    ],
    
    // E-Bike
    ebikeInfo: {
      chargingPoints: 'Tägliche Lademöglichkeit in allen Unterkünften',
      batteryRecommendation: 'Mind. 500 Wh Akkukapazität empfohlen',
      maxGradient: '12% (einzelne Passagen bis 15%)',
      spareBattery: 'Ersatzakku-Miete möglich (+150 €)'
    },
    
    // Sicherheit
    safety: {
      helmetMandatory: true,
      weatherCutoff: 'Tour kann bei Sturm/Starkregen angepasst werden',
      emergencyNumber: '+44 7700 900 XXX (24h Touren-Hotline)',
      equipmentList: '/downloads/equipment-nc500.pdf'
    },
    
    // Logistik
    logistics: {
      publicTransport: 'Inverness per Bahn ab Edinburgh (3.5h) oder Glasgow (3h)',
      parking: 'Kostenloser Langzeitparkplatz am Bahnhof verfügbar',
      accommodationTips: 'Vorabend-Übernachtung in Inverness empfohlen'
    },
    
    // Preis
    pricing: {
      base: 1890,
      ebikeSupplement: 350,
      singleRoomSupplement: 450,
      groupDiscount: '5% ab 4 Personen',
      additionalCosts: 'Ca. 400-500€ für Verpflegung'
    },
    
    // Verfügbarkeit
    availability: {
      openSpots: 8,
      minParticipants: 6,
      maxParticipants: 12,
      guaranteed: true
    },
    
    // Storno
    cancellation: {
      free: 'Bis 60 Tage vor Tour',
      fifty: '59-30 Tage: 50% Stornogebühr',
      full: 'Ab 29 Tage: 100% Stornogebühr',
      insurance: 'Reiserücktrittsversicherung empfohlen'
    },
    
    // Bewertungen
    rating: 4.8,
    reviewCount: 24,
    reviews: [
      { name: 'Sarah M.', rating: 5, date: '09/2024', text: 'Unvergessliche Tour! Guide war super, Unterkünfte top.' },
      { name: 'Thomas K.', rating: 5, date: '08/2024', text: 'Landschaft atemberaubend. Gut organisiert, klare Empfehlung!' }
    ]
  };

  return (
    <div className="tour-detail-page">
      <Link to="/routes" className="back-link">
        <ArrowLeft size={20} /> Zurück zu Routen
      </Link>

      {/* Hero Section */}
      <div className="tour-hero">
        <img src={tour.images[0]} alt={tour.title} />
        <div className="hero-overlay">
          <h1>{tour.title}</h1>
          <div className="hero-meta">
            <span><Calendar size={18} /> {tour.duration}</span>
            <span><MapPin size={18} /> {tour.distance} km</span>
            <span><TrendingUp size={18} /> +{tour.elevationGain}m</span>
            <span><Star size={18} /> {tour.rating} ({tour.reviewCount} Bewertungen)</span>
          </div>
        </div>
      </div>

      {/* Quick Info */}
      <div className="quick-info">
        <div className="info-card">
          <Calendar size={24} />
          <div>
            <h4>Nächster Termin</h4>
            <p>{tour.dates[0]}</p>
          </div>
        </div>
        <div className="info-card">
          <Users size={24} />
          <div>
            <h4>Verfügbarkeit</h4>
            <p>{tour.availability.openSpots} Plätze frei</p>
          </div>
        </div>
        <div className="info-card price-card">
          <h4>Preis</h4>
          <p className="price">€{tour.pricing.base}</p>
          <span>pro Person</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="tour-tabs">
        {['overview', 'route', 'requirements', 'services', 'logistics', 'booking'].map(tab => (
          <button
            key={tab}
            className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'overview' && 'Übersicht'}
            {tab === 'route' && 'Strecke'}
            {tab === 'requirements' && 'Anforderungen'}
            {tab === 'services' && 'Leistungen'}
            {tab === 'logistics' && 'Logistik'}
            {tab === 'booking' && 'Buchung'}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="tour-content">
        {activeTab === 'overview' && (
          <div className="tab-content">
            <section>
              <h2>Kurzbeschreibung</h2>
              <p>{tour.shortDescription}</p>
            </section>

            <section>
              <h2>Highlights</h2>
              <ul className="highlights-list">
                {tour.highlights.map((h, i) => (
                  <li key={i}><CheckCircle size={20} /> {h}</li>
                ))}
              </ul>
            </section>

            <section>
              <h2>Termine 2025</h2>
              <ul className="dates-list">
                {tour.dates.map((d, i) => (
                  <li key={i}><Calendar size={18} /> {d}</li>
                ))}
              </ul>
            </section>

            <section>
              <h2>Bildergalerie</h2>
              <div className="image-gallery">
                {tour.images.map((img, i) => (
                  <img key={i} src={img} alt={`Tour Bild ${i + 1}`} />
                ))}
              </div>
            </section>
          </div>
        )}

        {activeTab === 'route' && (
          <div className="tab-content">
            <section>
              <h2>Streckendaten</h2>
              <div className="data-grid">
                <div className="data-item">
                  <strong>Distanz:</strong> {tour.distance} km
                </div>
                <div className="data-item">
                  <strong>Höhenmeter:</strong> +{tour.elevationGain}m / -{tour.elevationLoss}m
                </div>
                <div className="data-item">
                  <strong>Untergrund:</strong> {tour.surface}
                </div>
                <div className="data-item">
                  <strong>Typ:</strong> {tour.routeType}
                </div>
              </div>
            </section>

            <section>
              <h2>GPX-Track Download</h2>
              <button className="download-btn">
                <Download size={18} /> NC500_Track.gpx herunterladen
              </button>
            </section>

            <section>
              <h2>E-Bike Hinweise</h2>
              <div className="info-box">
                <div><strong>Ladepunkte:</strong> {tour.ebikeInfo.chargingPoints}</div>
                <div><strong>Akkuempfehlung:</strong> {tour.ebikeInfo.batteryRecommendation}</div>
                <div><strong>Max. Steigung:</strong> {tour.ebikeInfo.maxGradient}</div>
                <div><strong>Ersatzakku:</strong> {tour.ebikeInfo.spareBattery}</div>
              </div>
            </section>
          </div>
        )}

        {activeTab === 'requirements' && (
          <div className="tab-content">
            <section>
              <h2>Schwierigkeitsgrad</h2>
              <div className="level-indicators">
                <div className="level-item">
                  <strong>Technik:</strong>
                  <div className="level-bars">
                    {[1,2,3,4,5].map(i => (
                      <div key={i} className={`bar ${i <= tour.technicalLevel ? 'filled' : ''}`} />
                    ))}
                  </div>
                  <span>{tour.technicalLevel}/5</span>
                </div>
                <div className="level-item">
                  <strong>Kondition:</strong>
                  <div className="level-bars">
                    {[1,2,3,4,5].map(i => (
                      <div key={i} className={`bar ${i <= tour.fitnessLevel ? 'filled' : ''}`} />
                    ))}
                  </div>
                  <span>{tour.fitnessLevel}/5</span>
                </div>
              </div>
            </section>

            <section>
              <h2>Voraussetzungen</h2>
              <ul>
                <li><strong>Mindestalter:</strong> {tour.minAge} Jahre</li>
                {tour.requirements.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </section>

            <section>
              <h2>Sicherheit</h2>
              <div className="safety-info">
                <div className={`safety-item ${tour.safety.helmetMandatory ? 'mandatory' : ''}`}>
                  <AlertCircle size={20} />
                  <div>
                    <strong>Helmpflicht:</strong> {tour.safety.helmetMandatory ? 'Ja' : 'Empfohlen'}
                  </div>
                </div>
                <div className="safety-item">
                  <strong>Wetter-Cutoffs:</strong> {tour.safety.weatherCutoff}
                </div>
                <div className="safety-item">
                  <Phone size={20} />
                  <strong>Notfall:</strong> {tour.safety.emergencyNumber}
                </div>
              </div>
            </section>
          </div>
        )}

        {activeTab === 'services' && (
          <div className="tab-content">
            <section>
              <h2>Im Preis inbegriffen</h2>
              <ul className="included-list">
                {tour.included.map((item, i) => (
                  <li key={i}><CheckCircle size={18} className="check-icon" /> {item}</li>
                ))}
              </ul>
            </section>

            <section>
              <h2>Nicht inbegriffen</h2>
              <ul className="excluded-list">
                {tour.excluded.map((item, i) => (
                  <li key={i}>• {item}</li>
                ))}
              </ul>
            </section>
          </div>
        )}

        {activeTab === 'logistics' && (
          <div className="tab-content">
            <section>
              <h2>Treffpunkt</h2>
              <p><MapPin size={18} /> {tour.meetingPoint}</p>
              <p><strong>Startzeit:</strong> {tour.startTime}</p>
              <p><strong>Rückkehrort:</strong> {tour.returnPoint}</p>
            </section>

            <section>
              <h2>Anreise</h2>
              <div><strong>ÖV:</strong> {tour.logistics.publicTransport}</div>
              <div><strong>Parken:</strong> {tour.logistics.parking}</div>
              <div><strong>Unterkunfts-Tipp:</strong> {tour.logistics.accommodationTips}</div>
            </section>
          </div>
        )}

        {activeTab === 'booking' && (
          <div className="tab-content">
            <section>
              <h2>Preisübersicht</h2>
              <div className="pricing-table">
                <div className="price-row">
                  <span>Basispreis</span>
                  <strong>€{tour.pricing.base}</strong>
                </div>
                <div className="price-row">
                  <span>E-Bike Miete (optional)</span>
                  <span>+€{tour.pricing.ebikeSupplement}</span>
                </div>
                <div className="price-row">
                  <span>Einzelzimmer-Zuschlag</span>
                  <span>+€{tour.pricing.singleRoomSupplement}</span>
                </div>
                <div className="price-row discount">
                  <span>Gruppenrabatt</span>
                  <span>{tour.pricing.groupDiscount}</span>
                </div>
              </div>
            </section>

            <section>
              <h2>Verfügbarkeit</h2>
              <div className="availability-info">
                <div><strong>Freie Plätze:</strong> {tour.availability.openSpots} von {tour.availability.maxParticipants}</div>
                <div><strong>Mindestteilnehmer:</strong> {tour.availability.minParticipants}</div>
                {tour.availability.guaranteed && (
                  <div className="guaranteed-badge">
                    <CheckCircle size={18} /> Durchführungsgarantie
                  </div>
                )}
              </div>
            </section>

            <section>
              <h2>Stornobedingungen</h2>
              <ul>
                <li>✓ {tour.cancellation.free}</li>
                <li>• {tour.cancellation.fifty}</li>
                <li>• {tour.cancellation.full}</li>
                <li><AlertCircle size={16} /> {tour.cancellation.insurance}</li>
              </ul>
            </section>

            <button className="book-now-btn">
              Jetzt buchen - €{tour.pricing.base}
            </button>
          </div>
        )}
      </div>

      {/* Reviews */}
      <div className="reviews-section">
        <h2>Bewertungen ({tour.reviewCount})</h2>
        <div className="reviews-grid">
          {tour.reviews.map((review, i) => (
            <div key={i} className="review-card">
              <div className="review-header">
                <strong>{review.name}</strong>
                <div className="rating">
                  {[1,2,3,4,5].map(s => (
                    <Star key={s} size={16} fill={s <= review.rating ? '#ffc107' : 'none'} />
                  ))}
                </div>
              </div>
              <p>{review.text}</p>
              <span className="review-date">{review.date}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
